import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Accordion, Alert, Spinner, Badge, InputGroup } from 'react-bootstrap';
import { Add, Remove, Delete, Download, PlayArrow, ArrowBack, Save, Info, Calculate, MenuBook, Edit, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { generateAccaQAAPI, generateAccaPDFAPI } from 'ServerAPI/serverAPI';
import { useToast } from 'context/toast-context/ToastProvider';
import renderLatex from "components/courses/add-new-course/steps/quiz-genaration/RenderLatex";

const AccaExamGeneration = ({ organizationId, selectedFile }) => {
    const { showToast } = useToast();
    const [step, setStep] = useState(0); // 0: Blueprint, 1: Edit & Preview
    const [isGeneratingQA, setIsGeneratingQA] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Metadata State
    const [metadata, setMetadata] = useState({
        paper_name: 'Financial Reporting',
        exam_level: 'Applied Skills',
        standards_framework: 'IFRS'
    });

    // Blueprint State
    const [blueprint, setBlueprint] = useState([
        {
            label: 'Section A',
            items: [{ type: 'OTQ_POOL', count: 15, marks: [2.0], bloom_taxonomy: 'create' }]
        }
    ]);

    // Dynamic Generated Data State
    const [examData, setExamData] = useState({});

    // Improved Stepper Component with Integrated Arrows
    const CustomStepper = ({ value, onChange, min = 0, label, step = 1, width = '100px' }) => (
        <div className="d-flex flex-column align-items-center">
            {label && <small className="text-muted mb-1 fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{label}</small>}
            <div className="d-flex align-items-center border rounded bg-white shadow-sm overflow-hidden" style={{ height: '34px', width }}>
                <div className="flex-grow-1 text-center fw-bold text-dark px-1 border-end h-100 d-flex align-items-center justify-content-center" style={{ fontSize: '14px' }}>
                    {value}
                </div>
                <div className="d-flex flex-column h-100 bg-light" style={{ width: '28px' }}>
                    <Button
                        variant="link"
                        className="p-0 d-flex align-items-center justify-content-center border-0 rounded-0 text-primary"
                        style={{ height: '50%', borderBottom: '1px solid #dee2e6' }}
                        onClick={() => onChange((parseFloat(value) || 0) + step)}
                    >
                        <KeyboardArrowUp style={{ fontSize: '16px' }} />
                    </Button>
                    <Button
                        variant="link"
                        className="p-0 d-flex align-items-center justify-content-center border-0 rounded-0 text-primary"
                        style={{ height: '50%' }}
                        onClick={() => onChange(Math.max(min, (parseFloat(value) || 0) - step))}
                    >
                        <KeyboardArrowDown style={{ fontSize: '16px' }} />
                    </Button>
                </div>
            </div>
        </div>
    );

    const EXAM_LEVELS = ['Applied Knowledge', 'Applied Skills', 'Strategic Professional'];
    const ITEM_TYPES = ['OTQ_POOL', 'OTQ_CASE', 'CONSTRUCTED_RESPONSE'];
    const BLOOM_LEVELS = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'];

    // Utility: Pre-process AI LaTeX to standard format
    const preProcessLatex = (text) => {
        if (!text) return "";
        let processed = text;

        // 1. Decode double-escaped unicode sequences (e.g. \u005C -> \)
        processed = processed.replace(/\\u([0-9A-Fa-f]{4})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
        );

        // 2. Fix double-escaped backslashes (\\\\ -> \) common in AI JSON responses
        processed = processed.replace(/\\\\/g, '\\');

        // 3. UNICODE PROTECTION FOR CURRENCY:
        // We use the Fullwidth Dollar '＄' (\uFF04) for all currency.
        // It looks like a dollar sign but does NOT trigger the LaTeX math parser.
        processed = processed.replace(/\\\$/g, '\uFF04');
        processed = processed.replace(/\\\\\$/g, '\uFF04');
        processed = processed.replace(/\$(?=\d)/g, '\uFF04');
        processed = processed.replace(/\$(?=\s|\)|$)/g, '\uFF04');

        // 4. NORMALIZE MATH DELIMITERS: 
        // Now it's safe to use standard $ for real math normalized from \( \)
        processed = processed.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$");
        processed = processed.replace(/\\\(/g, "$").replace(/\\\)/g, "$");

        // 5. SYMBOL CLEANUP: Convert LaTeX commands to clean Unicode fallbacks
        processed = processed.replace(/\\times/g, '×');
        processed = processed.replace(/\\div/g, '÷');
        processed = processed.replace(/\\%/g, '%');

        // 6. ARTIFACT CLEANUP: Fix literal %$ or \%
        processed = processed.replace(/(\d+%)\$/g, "$1");

        // 7. FINAL SAFETY: Balanced Math Check
        // If an odd number of $ symbols remain, it's a broken math block. 
        // We convert the last stray one to a Unicode dollar.
        const dollarCount = (processed.match(/\$/g) || []).length;
        if (dollarCount % 2 !== 0) {
            processed = processed.replace(/\$(?!.*\$)/, "\uFF04");
        }

        return processed;
    };

    // Utility: Clean option text
    const cleanOptionText = (text) => {
        if (!text) return "";
        return text.replace(/^[A-D](\.|\)|:)?\s+/i, "").trim();
    };

    // Blueprint Handlers
    const addSection = () => {
        setBlueprint([...blueprint, { label: `Section ${String.fromCharCode(65 + blueprint.length)}`, items: [] }]);
    };

    const removeSection = (index) => {
        const newBlueprint = blueprint.filter((_, i) => i !== index);
        setBlueprint([...newBlueprint]);
    };

    const addItemToSection = (sectionIndex, type) => {
        const newBlueprint = [...blueprint];
        const newItem = { type, bloom_taxonomy: 'create' };
        if (type === 'OTQ_POOL') {
            newItem.count = 5;
            newItem.marks = [2.0];
        } else if (type === 'OTQ_CASE') {
            newItem.count = 5;
            newItem.marks = [2, 2, 2, 2, 2];
        } else if (type === 'CONSTRUCTED_RESPONSE') {
            newItem.count = 3;
            newItem.marks = [10, 5, 5];
        }
        newBlueprint[sectionIndex].items.push(newItem);
        setBlueprint(newBlueprint);
    };

    const updateItem = (sectionIndex, itemIndex, field, value) => {
        const newBlueprint = [...blueprint];
        const item = newBlueprint[sectionIndex].items[itemIndex];

        if (field === 'count') {
            const newCount = parseInt(value) || 1;
            const currentMarks = Array.isArray(item.marks) ? item.marks : [2.0];
            const defaultMark = item.type === 'CONSTRUCTED_RESPONSE' ? 5 : 2;

            // Adjust marks array length based on count
            if (newCount > currentMarks.length) {
                const added = Array(newCount - currentMarks.length).fill(defaultMark);
                item.marks = [...currentMarks, ...added];
            } else {
                item.marks = currentMarks.slice(0, newCount);
            }
            item.count = newCount;
        } else {
            item[field] = value;
        }
        setBlueprint(newBlueprint);
    };

    const updateSubMark = (sectionIndex, itemIndex, markIndex, value) => {
        const newBlueprint = [...blueprint];
        const marks = [...newBlueprint[sectionIndex].items[itemIndex].marks];
        marks[markIndex] = parseFloat(value) || 0;
        newBlueprint[sectionIndex].items[itemIndex].marks = marks;
        setBlueprint(newBlueprint);
    };

    const removeItemFromSection = (sectionIndex, itemIndex) => {
        const newBlueprint = [...blueprint];
        newBlueprint[sectionIndex].items.splice(itemIndex, 1);
        setBlueprint(newBlueprint);
    };

    // Workflow Handlers
    const handleGenerateQA = async () => {
        try {
            setIsGeneratingQA(true);
            const payload = {
                ...metadata,
                pdf_filename: selectedFile?.name || 'ACCA_Exam_Mock.pdf',
                org_id: organizationId || "org_acca_training",
                blueprint: blueprint.map(section => ({
                    ...section,
                    items: section.items.map(item => ({ ...item, difficulty: 'hard' }))
                }))
            };

            const response = await generateAccaQAAPI(payload);
            const outerData = response?.data?.data;
            const finalData = outerData?.data || outerData || response?.data;

            if (finalData) {
                const sanitizedData = {};
                Object.keys(finalData).forEach(secKey => {
                    const items = finalData[secKey];
                    if (Array.isArray(items)) {
                        sanitizedData[secKey] = items.map(item => {
                            // Normalize scenario/case_study keys
                            let normalized = { ...item };
                            if (item.case_study && !item.scenario) {
                                normalized.scenario = item.case_study;
                            }

                            // Sanitize Standalone OTs
                            if (normalized.options) {
                                normalized.options = normalized.options.map(o => ({ ...o, text: cleanOptionText(o.text) }));
                            }
                            // Sanitize Case Study OTs
                            if (normalized.questions) {
                                normalized.questions = normalized.questions.map(q => ({
                                    ...q,
                                    options: q.options?.map(o => ({ ...o, text: cleanOptionText(o.text) }))
                                }));
                            }
                            return normalized;
                        });
                    }
                });
                setExamData(sanitizedData);
                setStep(1);
                showToast('Exam generated successfully!', 'success');
            }
        } catch (error) {
            console.error('Error generating QA:', error);
            showToast('Failed to generate exam', 'error');
        } finally {
            setIsGeneratingQA(false);
        }
    };

    const handleGeneratePDF = async () => {
        try {
            setIsGeneratingPDF(true);
            const payload = { paper_name: metadata.paper_name, exam_level: metadata.exam_level, data: examData };
            const response = await generateAccaPDFAPI(payload);
            if (response && response.data && response.data.pdf_content) {
                const byteCharacters = atob(response.data.pdf_content);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
                const file = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                window.open(URL.createObjectURL(file), '_blank');
            }
        } catch (error) {
            showToast('Failed to generate PDF', 'error');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const renderOTQ = (q, sectionLabel, qIdx) => (
        <Card key={qIdx} className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
                <Row className="mb-3">
                    <Col md={10}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <Form.Label className="fw-bold text-primary mb-0">Question {qIdx + 1}</Form.Label>
                            <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                const newData = { ...examData };
                                newData[sectionLabel][qIdx]._isEditing = !newData[sectionLabel][qIdx]._isEditing;
                                setExamData(newData);
                            }}>
                                {q._isEditing ? "Done" : <Edit fontSize="small" />}
                            </Button>
                        </div>
                        {q._isEditing ? (
                            <Form.Control as="textarea" rows={2} value={q.question} onChange={(e) => {
                                const newData = { ...examData }; newData[sectionLabel][qIdx].question = e.target.value; setExamData(newData);
                            }} className="mb-2" />
                        ) : (
                            <div className="mb-3 p-2 bg-white rounded border-start border-primary border-4 shadow-sm" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(q.question)) }} />
                        )}
                    </Col>
                    <Col md={2}>
                        <CustomStepper
                            label="Marks"
                            value={q.marks || 2}
                            step={0.5}
                            width="90px"
                            onChange={(val) => {
                                const newData = { ...examData }; newData[sectionLabel][qIdx].marks = val; setExamData(newData);
                            }}
                        />
                    </Col>
                </Row>
                <Row className="g-2 mb-3">
                    {q.options.map((opt, oIdx) => {
                        const optionLabel = String.fromCharCode(65 + oIdx);
                        const isCorrect = q.answer === optionLabel;
                        return (
                            <Col md={6} key={oIdx}>
                                <Card className={`p-2 transition-all ${isCorrect ? 'bg-success text-white border-success shadow' : 'bg-white'}`} style={{ minHeight: '80px' }}>
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Label className={`small fw-bold mb-0 ${isCorrect ? 'text-white' : 'text-muted'}`}>{optionLabel}</Form.Label>
                                            <Button variant="link" size="sm" className={`p-0 text-decoration-none ${isCorrect ? 'text-white' : ''}`} onClick={() => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][qIdx].options[oIdx]._isEditing = !newData[sectionLabel][qIdx].options[oIdx]._isEditing;
                                                setExamData(newData);
                                            }}>
                                                {opt._isEditing ? "Done" : <Edit style={{ fontSize: '14px' }} />}
                                            </Button>
                                        </div>
                                        {isCorrect && <Badge bg="light" text="success" pill style={{ fontSize: '0.6em' }}>Correct</Badge>}
                                    </div>
                                    {opt._isEditing ? (
                                        <Form.Control size="sm" value={opt.text} onChange={(e) => {
                                            const newData = { ...examData }; newData[sectionLabel][qIdx].options[oIdx].text = e.target.value; setExamData(newData);
                                        }} className="mb-1" />
                                    ) : (
                                        <div className={`fw-bold ${isCorrect ? 'text-white' : 'text-dark'}`} dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(opt.text)) }} />
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
                <div className="bg-white p-3 rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="small fw-bold text-info mb-0">Rationale</Form.Label>
                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                            const newData = { ...examData };
                            newData[sectionLabel][qIdx]._isEditingRationale = !newData[sectionLabel][qIdx]._isEditingRationale;
                            setExamData(newData);
                        }}>
                            {q._isEditingRationale ? "Done" : <Edit style={{ fontSize: '14px' }} />}
                        </Button>
                    </div>
                    {q._isEditingRationale ? (
                        <Form.Control as="textarea" rows={2} size="sm" value={q.explanation || ""} onChange={(e) => {
                            const newData = { ...examData }; newData[sectionLabel][qIdx].explanation = e.target.value; setExamData(newData);
                        }} />
                    ) : (
                        <div className="small text-muted" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(q.explanation || "")) }} />
                    )}
                </div>
            </Card.Body>
        </Card>
    );

    const renderCaseStudy = (sc, sectionLabel, scIdx) => {
        const updateScenario = (val) => {
            const newData = { ...examData };
            newData[sectionLabel][scIdx].scenario = val;
            setExamData(newData);
        };

        return (
            <Card key={scIdx} className="mb-4 border-success shadow-sm">
                <Card.Header className="bg-success text-white fw-bold">Case Study {scIdx + 1}</Card.Header>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="fw-bold text-primary mb-0">Scenario</Form.Label>
                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                            const newData = { ...examData };
                            newData[sectionLabel][scIdx]._isEditing = !newData[sectionLabel][scIdx]._isEditing;
                            setExamData(newData);
                        }}>
                            {sc._isEditing ? "Done" : <Edit fontSize="small" />}
                        </Button>
                    </div>
                    {sc._isEditing ? (
                        <Form.Control as="textarea" rows={4} className="mb-3" value={sc.scenario} onChange={(e) => updateScenario(e.target.value)} />
                    ) : (
                        <div className="mb-3 p-3 bg-light rounded border-start border-success border-4 shadow-sm" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(sc.scenario)) }} />
                    )}

                    {sc.questions?.map((q, qIdx) => (
                        <div key={qIdx} className="p-3 mb-4 bg-white border rounded shadow-sm">
                            <Row className="mb-2 align-items-center">
                                <Col md={10}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Form.Label className="small fw-bold text-success mb-0">OTQ {qIdx + 1}</Form.Label>
                                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                            const newData = { ...examData };
                                            newData[sectionLabel][scIdx].questions[qIdx]._isEditing = !newData[sectionLabel][scIdx].questions[qIdx]._isEditing;
                                            setExamData(newData);
                                        }}>
                                            {q._isEditing ? "Done" : <Edit style={{ fontSize: '14px' }} />}
                                        </Button>
                                    </div>
                                    {q._isEditing ? (
                                        <Form.Control
                                            value={q.question}
                                            className="mb-2"
                                            onChange={(e) => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][scIdx].questions[qIdx].question = e.target.value;
                                                setExamData(newData);
                                            }}
                                        />
                                    ) : (
                                        <div className="fw-bold mb-2" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(q.question)) }} />
                                    )}
                                </Col>
                                <Col md={2}>
                                    <CustomStepper
                                        label="Marks"
                                        value={q.marks || 2}
                                        step={0.5}
                                        width="90px"
                                        onChange={(val) => {
                                            const newData = { ...examData };
                                            newData[sectionLabel][scIdx].questions[qIdx].marks = val;
                                            setExamData(newData);
                                        }}
                                    />
                                </Col>
                            </Row>

                            <Row className="g-2 mb-3">
                                {q.options?.map((opt, oIdx) => {
                                    const optionLabel = String.fromCharCode(65 + oIdx);
                                    const isCorrect = q.answer === optionLabel;
                                    return (
                                        <Col md={6} key={oIdx}>
                                            <Card className={`p-2 mb-1 transition-all ${isCorrect ? 'bg-success text-white border-success' : 'bg-white shadow-sm border-light'}`} style={{ minHeight: '60px' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className={`small fw-bold ${isCorrect ? 'text-white' : 'text-muted'}`}>{optionLabel}</span>
                                                        <Button variant="link" size="sm" className={`p-0 text-decoration-none ${isCorrect ? 'text-white' : ''}`} onClick={() => {
                                                            const newData = { ...examData };
                                                            newData[sectionLabel][scIdx].questions[qIdx].options[oIdx]._isEditing = !newData[sectionLabel][scIdx].questions[qIdx].options[oIdx]._isEditing;
                                                            setExamData(newData);
                                                        }}>
                                                            {opt._isEditing ? "Done" : <Edit style={{ fontSize: '12px' }} />}
                                                        </Button>
                                                    </div>
                                                    {isCorrect && <Badge bg="light" text="success" pill style={{ fontSize: '0.6em' }}>Correct</Badge>}
                                                </div>
                                                {opt._isEditing ? (
                                                    <Form.Control size="sm" value={opt.text} onChange={(e) => {
                                                        const newData = { ...examData }; newData[sectionLabel][scIdx].questions[qIdx].options[oIdx].text = e.target.value; setExamData(newData);
                                                    }} className="mb-1" />
                                                ) : (
                                                    <div className={`fw-bold small ${isCorrect ? 'text-white' : 'text-dark'}`} dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(opt.text)) }} />
                                                )}
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>

                            <div className="bg-light p-2 rounded border small shadow-sm">
                                <Row>
                                    <Col md={12} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <Form.Label className="fw-bold text-info mb-0">Rationale</Form.Label>
                                            <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][scIdx].questions[qIdx]._isEditingRationale = !newData[sectionLabel][scIdx].questions[qIdx]._isEditingRationale;
                                                setExamData(newData);
                                            }}>
                                                {q._isEditingRationale ? "Done" : <Edit style={{ fontSize: '12px' }} />}
                                            </Button>
                                        </div>
                                        {q._isEditingRationale ? (
                                            <Form.Control as="textarea" rows={1} size="sm" value={q.explanation || ""} onChange={(e) => {
                                                const newData = { ...examData }; newData[sectionLabel][scIdx].questions[qIdx].explanation = e.target.value; setExamData(newData);
                                            }} />
                                        ) : (
                                            <div dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(q.explanation || "")) }} />
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    const renderConstructedResponse = (sc, sectionLabel, scIdx) => {
        const updateScenario = (val) => {
            const newData = { ...examData };
            newData[sectionLabel][scIdx].scenario = val;
            setExamData(newData);
        };

        return (
            <Card key={scIdx} className="mb-4 border-info shadow-sm">
                <Card.Header className="bg-info text-white fw-bold">Constructed Response {scIdx + 1}</Card.Header>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="fw-bold text-primary mb-0">Scenario</Form.Label>
                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                            const newData = { ...examData };
                            newData[sectionLabel][scIdx]._isEditing = !newData[sectionLabel][scIdx]._isEditing;
                            setExamData(newData);
                        }}>
                            {sc._isEditing ? "Done" : <Edit fontSize="small" />}
                        </Button>
                    </div>
                    {sc._isEditing ? (
                        <Form.Control as="textarea" rows={6} className="mb-3" value={sc.scenario} onChange={(e) => updateScenario(e.target.value)} />
                    ) : (
                        <div className="mb-3 p-3 bg-light rounded border-start border-info border-4 shadow-sm" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(sc.scenario)) }} />
                    )}

                    {sc.requirements?.map((req, rIdx) => (
                        <div key={rIdx} className="p-3 mb-3 bg-white border rounded shadow-sm">
                            <Row className="mb-2 align-items-center">
                                <Col md={1}>
                                    <Badge bg="info" className="p-2 w-100">{req.part || String.fromCharCode(97 + rIdx)}</Badge>
                                </Col>
                                <Col md={9}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <div className="fw-bold text-dark small">Requirement</div>
                                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                            const newData = { ...examData };
                                            newData[sectionLabel][scIdx].requirements[rIdx]._isEditing = !newData[sectionLabel][scIdx].requirements[rIdx]._isEditing;
                                            setExamData(newData);
                                        }}>
                                            {req._isEditing ? "Done" : <Edit fontSize="small" style={{ fontSize: '14px' }} />}
                                        </Button>
                                    </div>
                                    {req._isEditing ? (
                                        <Form.Control
                                            size="sm"
                                            value={req.requirement}
                                            onChange={(e) => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][scIdx].requirements[rIdx].requirement = e.target.value;
                                                setExamData(newData);
                                            }}
                                            className="mb-1"
                                        />
                                    ) : (
                                        <div className="p-1 bg-light rounded fw-bold" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(req.requirement)) }} />
                                    )}
                                </Col>
                                <Col md={2}>
                                    <CustomStepper
                                        label="Marks"
                                        value={req.marks}
                                        step={0.5}
                                        width="90px"
                                        onChange={(val) => {
                                            const newData = { ...examData };
                                            newData[sectionLabel][scIdx].requirements[rIdx].marks = val;
                                            setExamData(newData);
                                        }}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-3 bg-light p-2 rounded border small">
                                <Row>
                                    <Col md={12} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <Form.Label className="fw-bold text-info mb-0">Model Answer</Form.Label>
                                            <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][scIdx].requirements[rIdx]._isEditingModel = !newData[sectionLabel][scIdx].requirements[rIdx]._isEditingModel;
                                                setExamData(newData);
                                            }}>
                                                {req._isEditingModel ? "Done" : <Edit style={{ fontSize: '12px' }} />}
                                            </Button>
                                        </div>
                                        {req._isEditingModel ? (
                                            <Form.Control
                                                as="textarea" rows={2} size="sm"
                                                value={req.model_answer || ""}
                                                onChange={(e) => {
                                                    const newData = { ...examData };
                                                    newData[sectionLabel][scIdx].requirements[rIdx].model_answer = e.target.value;
                                                    setExamData(newData);
                                                }}
                                            />
                                        ) : (
                                            <div className="p-1 bg-white border rounded" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(req.model_answer || "")) }} />
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <Form.Label className="fw-bold text-success mb-0">Step-by-Step Working</Form.Label>
                                            <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                                                const newData = { ...examData };
                                                newData[sectionLabel][scIdx].requirements[rIdx]._isEditingWorking = !newData[sectionLabel][scIdx].requirements[rIdx]._isEditingWorking;
                                                setExamData(newData);
                                            }}>
                                                {req._isEditingWorking ? "Done" : <Edit style={{ fontSize: '12px' }} />}
                                            </Button>
                                        </div>
                                        {req._isEditingWorking ? (
                                            <Form.Control
                                                as="textarea" rows={2} size="sm"
                                                value={req.working || ""}
                                                onChange={(e) => {
                                                    const newData = { ...examData };
                                                    newData[sectionLabel][scIdx].requirements[rIdx].working = e.target.value;
                                                    setExamData(newData);
                                                }}
                                            />
                                        ) : (
                                            <div className="p-1 bg-white border rounded" dangerouslySetInnerHTML={{ __html: renderLatex(preProcessLatex(req.working || "")) }} />
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    const renderBlueprintStep = () => (
        <>
            <Row className="mb-4">
                <Col md={4}><Form.Label className="fw-semibold">Level</Form.Label><Form.Select value={metadata.exam_level} onChange={(e) => setMetadata({ ...metadata, exam_level: e.target.value })}>{EXAM_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}</Form.Select></Col>
                <Col md={4}><Form.Label className="fw-semibold">Paper</Form.Label><Form.Control type="text" value={metadata.paper_name} onChange={(e) => setMetadata({ ...metadata, paper_name: e.target.value })} /></Col>
                <Col md={4}><Form.Label className="fw-semibold">Framework</Form.Label><Form.Control type="text" value={metadata.standards_framework} onChange={(e) => setMetadata({ ...metadata, standards_framework: e.target.value })} /></Col>
            </Row>
            {blueprint.map((section, sIdx) => (
                <Card key={sIdx} className="mb-3 border-light shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                        <Form.Control size="sm" type="text" value={section.label} onChange={(e) => { const nb = [...blueprint]; nb[sIdx].label = e.target.value; setBlueprint(nb); }} style={{ maxWidth: '250px', fontWeight: 'bold' }} />
                        <Button variant="outline-danger" size="sm" onClick={() => removeSection(sIdx)}><Delete fontSize="small" /></Button>
                    </Card.Header>
                    <Card.Body>
                        {section.items.map((item, iIdx) => (
                            <div key={iIdx} className="mb-3 p-3 border rounded bg-white">
                                <Row className="align-items-center g-3">
                                    <Col md={2}><Badge bg="info" className="p-2 w-100">{item.type.replace('_', ' ')}</Badge></Col>
                                    <Col md={2}>
                                        <small className="text-muted mb-1 fw-bold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Bloom's Taxonomy Levels</small>
                                        <Form.Select size="sm" value={item.bloom_taxonomy} onChange={(e) => updateItem(sIdx, iIdx, 'bloom_taxonomy', e.target.value)}>{BLOOM_LEVELS.map(b => <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>)}</Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <CustomStepper
                                            label="Count"
                                            value={item.count || 1}
                                            min={1}
                                            onChange={(val) => updateItem(sIdx, iIdx, 'count', val)}
                                        />
                                    </Col>
                                    <Col md={5}>
                                        <div className="d-flex gap-3 flex-wrap align-items-end justify-content-start">
                                            {item.type === 'OTQ_POOL' ? (
                                                <CustomStepper
                                                    label="Marks / Q"
                                                    value={item.marks[0] || 2}
                                                    step={0.5}
                                                    onChange={(val) => {
                                                        const nb = [...blueprint];
                                                        nb[sIdx].items[iIdx].marks = [val];
                                                        setBlueprint(nb);
                                                    }}
                                                />
                                            ) : (
                                                item.marks.map((m, mIdx) => (
                                                    <CustomStepper
                                                        key={mIdx}
                                                        label={`Q${mIdx + 1}`}
                                                        value={m}
                                                        step={0.5}
                                                        width="85px"
                                                        onChange={(val) => updateSubMark(sIdx, iIdx, mIdx, val)}
                                                    />
                                                ))
                                            )}
                                            <div className="ms-auto pb-1">
                                                <Button
                                                    variant="link"
                                                    className="text-danger p-0"
                                                    onClick={() => removeItemFromSection(sIdx, iIdx)}
                                                    title="Remove Item"
                                                >
                                                    <Delete />
                                                </Button>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                        <div className="d-flex gap-2 mt-2">
                            {ITEM_TYPES.map(type => <Button key={type} variant="outline-secondary" size="sm" onClick={() => addItemToSection(sIdx, type)}>+ {type.replace('_', ' ')}</Button>)}
                        </div>
                    </Card.Body>
                </Card>
            ))}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold text-primary mb-0 pb-2 border-bottom">Blueprint Configuration</h5>
                <Button variant="outline-primary" size="sm" className="fw-bold" onClick={addSection}>+ Add New Section</Button>
            </div>
            <div className="text-center mt-4"><Button variant="primary" size="lg" onClick={handleGenerateQA} disabled={isGeneratingQA || blueprint.length === 0 || blueprint.some(s => s.items.length === 0)}>{isGeneratingQA ? <Spinner size="sm" /> : 'Generate Exam Data'}</Button></div>
        </>
    );

    return (
        <div className="acca-generation-container">
            <h5 className="fw-bold text-primary mb-4 pb-2 border-bottom">ACCA Exam Studio <Badge bg="primary" className="ms-2">Step {step + 1}</Badge></h5>
            {step === 0 ? renderBlueprintStep() : (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                        <Button variant="outline-secondary" onClick={() => setStep(0)}><ArrowBack className="me-1" /> Back to Blueprint</Button>
                        <div className="text-muted small">Review and refine the generated exam content below.</div>
                    </div>

                    <Accordion defaultActiveKey="0" className="mb-4 shadow-sm">
                        {Object.entries(examData).map(([sec, items], sIdx) => (
                            <Accordion.Item eventKey={sIdx.toString()} key={sIdx}>
                                <Accordion.Header className="fw-bold">{sec} ({items.length} Items)</Accordion.Header>
                                <Accordion.Body className="bg-white">
                                    {items.map((item, idx) => {
                                        if (item.options) {
                                            return renderOTQ(item, sec, idx);
                                        } else if (item.questions) {
                                            return renderCaseStudy(item, sec, idx);
                                        } else if (item.requirements) {
                                            return renderConstructedResponse(item, sec, idx);
                                        }
                                        return <Alert variant="warning" key={idx}>Unknown Content Format</Alert>;
                                    })}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>

                    {/* <Card className="border-success bg-light shadow-sm mb-5">
                        <Card.Body className="text-center p-4">
                            <h5 className="fw-bold text-success mb-2">Final Step</h5>
                            <p className="text-muted mb-4">Review complete? Click below to generate the professional ACCA PDF document.</p>
                            <Button variant="success" size="lg" className="px-5 shadow" onClick={handleGeneratePDF} disabled={isGeneratingPDF}>
                                {isGeneratingPDF ? (
                                    <><Spinner size="sm" className="me-2" /> Creating PDF...</>
                                ) : (
                                    <><Download className="me-2" /> Generate Final Exam PDF</>
                                )}
                            </Button>
                        </Card.Body>
                    </Card> */}
                </>
            )}
        </div>
    );
};

export default AccaExamGeneration;
